import Button from '@/common/components/atom/button';
import { Toman } from '@/common/components/atom/toman/toman';
import CheckIcon from '@/common/components/icons/check';
import ChevronIcon from '@/common/components/icons/chevron';
import { addCommas } from '@persian-tools/persian-tools';
import { formatToman, getCoverage } from '../functions/coverage';
import type { CorporateDoctor, CorporatePlan } from '../types';

export const CORPORATE_PAYMENT_METHOD = 'corporate_credit';

/** One small line under the credit's name: what is left of it, as the contract counts it. */
export const corporateCreditLeft = (plan: CorporatePlan) => {
  switch (plan.kind) {
    case 'quota':
      return plan.remaining > 0 ? `${plan.remaining} ویزیت رایگان باقی‌مانده` : `سهمیهٔ این دوره تمام شده؛ تمدید ${plan.renewsOn}`;
    case 'cap':
      return `تا ${formatToman(plan.capToman)} تومان از هزینهٔ هر ویزیت`;
    default:
      return `هزینهٔ ویزیت با ${plan.organization.name}`;
  }
};

/**
 * The organization credit as one more entry of the factor's «روش پرداخت» list, in the shape the
 * payment-methods API already returns (title, html_content, display_amount), so a backend can send it
 * with no new UI: the name, and under it one line on what is left. Choosing the bank keeps the credit.
 */
export const corporatePaymentMethodFor = (plan: CorporatePlan) => ({
  payment_method: CORPORATE_PAYMENT_METHOD,
  title: `اعتبار سازمانی ${plan.organization.name}`,
  logo: '',
  html_content: corporateCreditLeft(plan),
  display_amount: '',
});

/** Same, for the design preview's sample doctors. */
export const corporatePaymentMethod = (plan: CorporatePlan, doctor: CorporateDoctor) => {
  const coverage = getCoverage(plan, doctor, true);
  return {
    ...corporatePaymentMethodFor(plan),
    // only when the patient still pays something, so the remaining amount is visible on the choice
    display_amount: coverage.status === 'partial' ? `${formatToman(coverage.payableToman)} تومان` : '',
  };
};

/**
 * The factor's bottom bar (`FactorWrapper`), unchanged except for the label: with nothing to pay now
 * the button uses the copy the factor already has for that case («شروع ویزیت»).
 */
export const CorporatePayBar = ({ payableToman, onSubmit }: { payableToman: number; onSubmit?: () => void }) => (
  <div className="sticky bottom-0 z-30 flex w-full items-center justify-between gap-6 border-t border-slate-200 bg-slate-50 px-4 pb-4 pt-4 shadow-[0_-6px_20px_rgba(15,23,42,0.16)]">
    <div className="flex shrink-0 flex-col items-start gap-1 text-start">
      <span className="text-xs text-slate-500">مبلغ قابل پرداخت</span>
      <span className="inline-flex items-center gap-1 text-slate-900">
        <strong className="text-base font-bold tabular-nums sm:text-lg">{addCommas(payableToman)}</strong>
        <Toman className="text-xs" />
      </span>
    </div>
    <Button onClick={onSubmit} className="min-h-[48px] max-w-[65%] flex-1 !w-auto !px-4 !text-sm !font-semibold">
      {payableToman === 0 ? 'شروع ویزیت' : 'پرداخت و شروع ویزیت'}
      <ChevronIcon dir="left" className="ms-2" aria-hidden="true" />
    </Button>
  </div>
);

interface BookedProps {
  plan: CorporatePlan;
  doctor: CorporateDoctor;
  patientName: string;
  payableToman: number;
  avatarSrc: string;
}

/** After «شروع ویزیت»: the visit is booked and the chat is one tap away — the patient never left the site. */
export const CorporateBooked = ({ plan, doctor, patientName, payableToman, avatarSrc }: BookedProps) => {
  const rows: [string, React.ReactNode][] = [
    ['بیمار', patientName],
    ['گفت‌وگو در', 'پیام‌رسان پذیرش۲۴'],
    ['شروع ویزیت', 'حدود ۲۰ دقیقه دیگر'],
    [
      'پرداخت شما',
      payableToman === 0 ? (
        <span className="text-green-600">رایگان، با اعتبار {plan.organization.name}</span>
      ) : (
        <span className="inline-flex items-center gap-1">
          {addCommas(payableToman)} <Toman className="text-xs" />
        </span>
      ),
    ],
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col items-center gap-3 bg-white px-5 pb-6 pt-8 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-green-600 text-white">
          <CheckIcon className="h-7 w-7" />
        </span>
        <h1 className="text-lg font-bold text-slate-900">ویزیت آنلاین شما ثبت شد</h1>
        <p className="text-sm leading-6 text-slate-600">پزشک پیام شما را در پیام‌رسان پذیرش۲۴ می‌بیند و همان‌جا پاسخ می‌دهد.</p>
      </div>
      <div className="flex flex-col gap-4 bg-white p-4">
        <div className="flex items-center gap-3">
          <img src={avatarSrc} alt="" className="h-12 w-12 rounded-full" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-900">{doctor.name}</span>
            <span className="text-xs text-slate-500">{doctor.expertise}</span>
          </div>
        </div>
        <dl className="flex flex-col divide-y divide-slate-100 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between py-3">
              <dt className="text-slate-500">{label}</dt>
              <dd className="font-medium text-slate-800">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="bg-white p-4">
        <Button block className="!font-semibold">
          ورود به گفت‌وگو با پزشک
        </Button>
      </div>
    </div>
  );
};
